import { useTranslation } from 'react-i18next';
import {
  type KnownModelCompatibilityFinding,
  getChangeTypeTranslationKey,
  getReasonCodeTranslationKey,
  isKnownModelCompatibilityFinding,
} from '@/tools/models/compatibilityDiff.utilities';
import { Icon } from '@seeqdev/qomponents';
import type { ModelCompatibilityFindingOutputV1 } from '@seeqdev/api/model/ModelCompatibilityFindingOutputV1';

export interface CompatibilityFindingsProps {
  findings: ModelCompatibilityFindingOutputV1[];
  featureNamesByCanonicalId: Map<string, string>;
}

/**
 * Renders grouped compatibility findings with progressive disclosure.
 * ERROR findings are shown in a "Breaking changes" section (expanded by default).
 * INFO findings are shown in an "Other changes" section (collapsed by default).
 *
 * Each finding row carries a subtle change-type tag (added / modified / deleted)
 * on the right, and breaking rows get an amber left-border accent.
 *
 * This is a pure display component — no acknowledgment logic. Callers compose it
 * into modals (CompatibilityReportModal, LockModelConfirmation) alongside their
 * own acknowledgment UIs.
 */
export const CompatibilityFindings = ({ findings, featureNamesByCanonicalId }: CompatibilityFindingsProps) => {
  const { t } = useTranslation();

  const knownFindings = findings.filter(isKnownModelCompatibilityFinding);
  const errorFindings = knownFindings.filter((f) => f.severity === 'ERROR');
  const infoFindings = knownFindings.filter((f) => f.severity === 'INFO');

  const resolveFeatureName = (canonicalId: string | undefined): string => {
    const name = canonicalId ? featureNamesByCanonicalId.get(canonicalId) : undefined;
    if (name) return name;
    // A missing entry means the upstream data path didn't populate the map for this finding.
    // Rather than render a raw GUID, show a neutral placeholder and surface the issue in dev.
    if (canonicalId && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`CompatibilityFindings: missing feature name for canonicalId ${canonicalId}`);
    }
    return t('MODELS.COMPATIBILITY_FINDING_UNKNOWN_FEATURE');
  };

  if (knownFindings.length === 0) return null;

  return (
    <div className="tw:flex tw:flex-col tw:gap-3.5 tw:w-full">
      {errorFindings.length > 0 && (
        <FindingsSection
          testId="compatibilityBreakingSection"
          defaultOpen={true}
          label={t('MODELS.COMPATIBILITY_BREAKING_SECTION')}
          count={errorFindings.length}
          iconName="fa-exclamation-triangle"
          headerClassNames="tw:text-sq-error">
          {errorFindings.map((finding, i) => (
            <FindingRow
              key={`error-${i}`}
              finding={finding}
              featureName={resolveFeatureName(finding.featureCanonicalId)}
              isBreaking={true}
            />
          ))}
        </FindingsSection>
      )}

      {infoFindings.length > 0 && (
        <FindingsSection
          testId="compatibilityInfoSection"
          defaultOpen={false}
          label={t('MODELS.COMPATIBILITY_INFO_SECTION')}
          count={infoFindings.length}
          iconName="fa-info-circle"
          headerClassNames="tw:text-sq-text-secondary">
          {infoFindings.map((finding, i) => (
            <FindingRow
              key={`info-${i}`}
              finding={finding}
              featureName={resolveFeatureName(finding.featureCanonicalId)}
              isBreaking={false}
            />
          ))}
        </FindingsSection>
      )}
    </div>
  );
};

interface FindingsSectionProps {
  testId: string;
  defaultOpen: boolean;
  label: string;
  count: number;
  iconName: string;
  headerClassNames: string;
  children: React.ReactNode;
}

const FindingsSection = ({
  testId,
  defaultOpen,
  label,
  count,
  iconName,
  headerClassNames,
  children,
}: FindingsSectionProps) => (
  <details open={defaultOpen} data-testid={testId} className="tw:group tw:w-full">
    <summary className="tw:cursor-pointer tw:select-none tw:list-none tw:flex tw:items-center tw:gap-1.5 tw:py-1 [&::-webkit-details-marker]:tw:hidden">
      <Icon icon={iconName} type="theme" extraClassNames="tw:text-sm" />
      <span className={`tw:font-semibold tw:text-sm tw:tracking-tight ${headerClassNames}`}>{label}</span>
      <span className={`tw:text-sm tw:font-normal tw:opacity-70 ${headerClassNames}`}>({count})</span>
      <Icon
        icon="fa-chevron-right"
        type="theme"
        extraClassNames="tw:text-[0.625rem] tw:ml-0.5 tw:opacity-60 tw:transition-transform tw:duration-150 tw:group-open:rotate-90"
      />
    </summary>
    <ul className="tw:mt-1.5 tw:flex tw:flex-col tw:gap-0.5 tw:list-none tw:pl-0 tw:m-0 tw:w-full">{children}</ul>
  </details>
);

interface FindingRowProps {
  finding: KnownModelCompatibilityFinding;
  featureName: string;
  isBreaking: boolean;
}

const FindingRow = ({ finding, featureName, isBreaking }: FindingRowProps) => {
  const { t } = useTranslation();
  const messageKey = getReasonCodeTranslationKey(finding.reasonCode);
  const changeTypeKey = getChangeTypeTranslationKey(finding.changeType);

  const rowBase =
    'tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full tw:rounded-r-sm tw:border-l-[3px] tw:pl-2.5 tw:pr-2.5 tw:py-2 tw:text-sm tw:text-sq-text-color tw:dark:text-sq-dark-text';
  const rowTone = isBreaking
    ? 'tw:bg-sq-bg-warning/40 tw:dark:bg-amber-950/30 tw:border-sq-bg-warning'
    : 'tw:bg-[#f8f6f2] tw:dark:bg-sq-gray-900/40 tw:border-transparent';

  return (
    <li className={`${rowBase} ${rowTone}`}>
      <span className="tw:flex-1">{t(messageKey, { featureName })}</span>
      <span
        className="tw:shrink-0 tw:opacity-60 tw:text-[0.625rem] tw:font-medium tw:uppercase tw:tracking-wide tw:text-sq-text-secondary tw:border tw:border-sq-text-secondary tw:rounded-sm tw:px-1.5 tw:py-[1px] tw:leading-none"
        data-testid="compatibilityChangeTypeTag">
        {t(changeTypeKey)}
      </span>
    </li>
  );
};
